import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { RouteErrorBoundary } from "./components/elements/ErrorBoundary/ErrorBoundary";
import { AuthProvider, useAuth } from "./context/authContext";
import LoginPage from "./artifacts/auth/pages/Login";
import SuccessPage from "./artifacts/auth/pages/Success";
import ErrorPage from "./artifacts/auth/pages/Error";
import { useView, ViewProvider, type ViewRole } from "./context/viewContext";
import AdminDashboard from "./artifacts/admin/pages/Dashboard";
import AdminRotations from "./artifacts/admin/pages/Rotations";
import AdminExplorations from "./artifacts/admin/pages/Explorations";
import AdminAssign from "./artifacts/admin/pages/Assign";
import AdminEmails from "./artifacts/admin/pages/Emails";
import AdminUsers from "./artifacts/admin/pages/Users";
import AdminExports from "./artifacts/admin/pages/Exports";
import AdminErrorLogs from "./artifacts/admin/pages/ErrorLogs";
import type { JSX } from "react";
import TeacherDashboard from "./artifacts/teacher/pages/Dashboard";
import TeacherBrowseExplorations from "./artifacts/teacher/pages/BrowseExplorations";
import TeacherRotations from "./artifacts/teacher/pages/Rotations";
import TeacherMyExplorations from "./artifacts/teacher/pages/MyExplorations";
import TeacherAdvisees from "./artifacts/teacher/pages/Advisees";
import AttendancePage from "./artifacts/global/pages/Attendance";
import StudentDashboard from "./artifacts/student/pages/Dashboard";
import StudentBrowseExplorations from "./artifacts/student/pages/BrowseExplorations";
import StudentMyExplorations from "./artifacts/student/pages/MyExplorations";
import StudentSignUp from "./artifacts/student/pages/SignUp";

const SysAdminDashboard = () => <AdminDashboard sysAdmin />;
const SysAdminEmails = () => <AdminEmails sysAdmin />;
const SysAdminRotations = () => <AdminRotations sysAdmin />;
const SysAdminRotationsArchive = () => <AdminRotations archived sysAdmin />;
const AdminRotationsArchive = () => <AdminRotations archived />;
const TeacherRotationsArchive = () => <TeacherRotations archived />;
const SysAdminExplorations = () => <AdminExplorations sysAdmin />;
const SysAdminExplorationsArchive = () => (
  <AdminExplorations archived sysAdmin />
);
const AdminExplorationsArchive = () => <AdminExplorations archived />;
const TeacherBrowseExplorationsArchive = () => (
  <TeacherBrowseExplorations archived />
);
const TeacherMyExplorationsArchive = () => <TeacherMyExplorations archived />;
const StudentMyExplorationsArchive = () => <StudentMyExplorations archived />;

const AppRouter = () => {
  return (
    <AuthProvider>
      <ViewProvider>
        <BrowserRouter>
          <RouteErrorBoundary>
            <Routes>
              <Route
                path="/login"
                element={
                  <NoAuthRoute>
                    <LoginPage />
                  </NoAuthRoute>
                }
              />
              <Route
                path="/auth/success"
                element={
                  <NoAuthRoute>
                    <SuccessPage />
                  </NoAuthRoute>
                }
              />
              <Route
                path="/auth/error"
                element={
                  <NoAuthRoute>
                    <ErrorPage />
                  </NoAuthRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ViewRoute
                    fallback="/login"
                    views={{
                      "sys-admin": <SysAdminDashboard />,
                      administrator: <AdminDashboard />,
                      teacher: <TeacherDashboard />,
                      student: <StudentDashboard />,
                    }}
                  />
                }
              />
              <Route
                path="/rotations"
                element={
                  <ViewRoute
                    views={{
                      "sys-admin": <SysAdminRotations />,
                      administrator: <AdminRotations />,
                      teacher: <TeacherRotations />,
                      student: <StudentSignUp />,
                    }}
                  />
                }
              />
              <Route
                path="/explorations"
                element={
                  <ViewRoute
                    views={{
                      "sys-admin": <SysAdminExplorations />,
                      administrator: <AdminExplorations />,
                      teacher: <TeacherBrowseExplorations />,
                      student: <StudentBrowseExplorations />,
                    }}
                  />
                }
              />
              <Route
                path="/explorations/me"
                element={
                  <ViewRoute
                    views={{
                      teacher: <TeacherMyExplorations />,
                      student: <StudentMyExplorations />,
                    }}
                  />
                }
              />
              <Route
                path="/advisees"
                element={
                  <ViewRoute
                    views={{
                      teacher: <TeacherAdvisees />,
                    }}
                  />
                }
              />
              <Route
                path="/assign"
                element={
                  <ViewRoute
                    views={{
                      administrator: <AdminAssign />,
                    }}
                  />
                }
              />
              <Route
                path="/emails"
                element={
                  <ViewRoute
                    views={{
                      "sys-admin": <SysAdminEmails />,
                      administrator: <AdminEmails />,
                    }}
                  />
                }
              />
              <Route
                path="/users"
                element={
                  <ViewRoute
                    views={{
                      "sys-admin": <AdminUsers />,
                    }}
                  />
                }
              />
              <Route
                path="/exports"
                element={
                  <ViewRoute
                    views={{
                      "sys-admin": <AdminExports />,
                    }}
                  />
                }
              />
              <Route
                path="/error-logs"
                element={
                  <ViewRoute
                    views={{
                      "sys-admin": <AdminErrorLogs />,
                    }}
                  />
                }
              />
              <Route
                path="/attendance/:classId"
                element={
                  <ViewRoute
                    views={{
                      "sys-admin": <AttendancePage />,
                      administrator: <AttendancePage />,
                      teacher: <AttendancePage />,
                    }}
                  />
                }
              />

              <Route
                path="/rotations/archive"
                element={
                  <ViewRoute
                    views={{
                      "sys-admin": <SysAdminRotationsArchive />,
                      administrator: <AdminRotationsArchive />,
                      teacher: <TeacherRotationsArchive />,
                      student: <Navigate to="/dashboard" replace />,
                    }}
                  />
                }
              />
              <Route
                path="/explorations/archive"
                element={
                  <ViewRoute
                    views={{
                      "sys-admin": <SysAdminExplorationsArchive />,
                      administrator: <AdminExplorationsArchive />,
                      teacher: <TeacherBrowseExplorationsArchive />,
                    }}
                  />
                }
              />
              <Route
                path="/explorations/me/archive"
                element={
                  <ViewRoute
                    views={{
                      teacher: <TeacherMyExplorationsArchive />,
                      student: <StudentMyExplorationsArchive />,
                    }}
                  />
                }
              />

              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </RouteErrorBoundary>
        </BrowserRouter>
      </ViewProvider>
    </AuthProvider>
  );
};

export { AppRouter };

const ViewRoute = <T extends JSX.Element>({
  views,
  fallback = "/dashboard",
}: {
  views: Partial<Record<ViewRole, T>>;
  fallback?: string;
}) => {
  const { viewAs } = useView();
  const { state, dispatch } = useAuth();

  if (!state.user) {
    return <Navigate to="/login" replace />;
  }
  if (
    !state.user.role ||
    !["sys-admin", "administrator", "teacher", "student"].includes(
      state.user.role,
    )
  ) {
    dispatch({ type: "LOGOUT" });
    return <Navigate to="/auth/error" replace />;
  }

  const Component = views[viewAs];
  if (!Component) {
    return <Navigate to={fallback} replace />;
  }

  return Component;
};

const NoAuthRoute = ({
  children,
  redirectTo = "/dashboard",
}: {
  children: JSX.Element;
  redirectTo?: string;
}) => {
  const { state } = useAuth();

  if (state.accessToken) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};
